import { MessageBus } from '@podium/browser';
import IconChevronLeft16 from '@warp-ds/icons/react/chevron-left-16';
import IconChevronLeft24 from '@warp-ds/icons/react/chevron-left-24';
import IconChevronRight24 from '@warp-ds/icons/react/chevron-right-24';
import IconClose16 from '@warp-ds/icons/react/close-16';
import IconGrid24 from '@warp-ds/icons/react/grid-24';
import IconImage24 from '@warp-ds/icons/react/image-24';
import { Button, Tab, TabPanel, Tabs } from '@warp-ds/react';
import { useRouter } from 'next/router';
import type React from 'react';
import type { MutableRefObject, UIEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import { ActionBar } from './components/ActionBar';
import { DarkDynamicIsland } from './components/DarkDynamicIsland';
import { ImagesOverview } from './components/ImagesOverview';
import { Lightbox } from './components/Lightbox';
import { MainCarousel } from './components/MainCarousel';
import { MobileFullscreenCarousel } from './components/MobileFullscreenCarousel';
import type { AdImage, LightboxState, SmoothType } from './types';
import { debounce, mod, throttle } from './utils';

import './ImageGallery.css';

export const ImageGallery = (props: ImageGalleryProps) => {
    const {
        itemId,
        userId,
        images,
        smoothOverride,
        initialImage,
        favoriteHeartButton,
        programmaticRatioOverride,
        fallbackUrl,
        onChange,
        useBackground,
        roundAllCorners,
        title,
        isMobile,
        showDescription = true,
    } = props;

    const router = useRouter();

    const handleHistory = () => {
        router.back();
    };

    const [state, setState]: [State, SetState] = useState<State>({
        lightboxState: 'closed',
        currentImage: (initialImage && Math.min(initialImage, images.length - 1)) || 0,
        currentFloorPlanImage: 0,
        smooth: smoothOverride !== undefined ? smoothOverride : 'throttled',
        programmaticRatio: programmaticRatioOverride || false,
        tab: 'images',
        pill: 'Galleri',
    });

    const ignorePopstate = useRef(false);

    useEffect(() => {
        const handlePopState = (_event: PopStateEvent) => {
            if (state.lightboxState === 'big-image' && ignorePopstate.current === false) {
                toggleLightbox(cancelDelayF, state, setState, isMobile, 'images', 'Galleri', handleHistory, ignorePopstate, true)();
                setFullscreen(false);
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [state.lightboxState]);

    const [fullscreen, setFullscreen] = useState(false);

    const showMap = false; // images.length > 0;
    const showTabs = false; // showMap || showVideoButton || showVirtualViewingButton || hasFloorPlanImages;
    const showPanel = images.length > 1;

    // send events up
    useEffect(() => {
        onChange?.(state.lightboxState);
    }, [state, onChange]);

    const imageCount = images.length;

    const [delayF, cancelDelayF] = state.smooth === 'smooth' ? [throttleCurrent, cancelThrottle] : [debounceCurrent, cancelDebounce];

    useEffect(() => {
        if (!smoothOverride && !programmaticRatioOverride) {
            let programmaticRatio = false;
            let smooth: SmoothType = state.smooth;

            try {
                // Relatively new css prop. Does not work in Safari < 15
                const supportsAspectRatio =
                    typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('aspect-ratio', 'auto');
                if (!supportsAspectRatio) {
                    programmaticRatio = true;
                }

                // On Android, the snapping of css works with varying degree, but it is certainly safer just to
                // turn it off. Since we are defaulting to throttle, we could allow it...
                /*
                if (
                    navigator.userAgent.toLowerCase().indexOf('android') !== -1
                ) {
                    smooth = 'no-scroll';
                }*/
            } catch (e) {
                // Shouldn't happen, but...
                console.error('Error in feature detection', e);
                smooth = 'no-scroll';
                programmaticRatio = true;
            }

            setState((s) => ({ ...s, smooth, programmaticRatio }));
        }
    }, []);

    useEffect(() => {
        if (state.tab === 'floorplan') {
            const floorPlanImageCount = 0;
            const listener = handlePagingKeyDownFloor(
                delayF,
                cancelDelayF,
                state,
                setState,
                floorPlanImageCount,
                handleHistory,
                ignorePopstate,
            );
            document.addEventListener('keydown', listener);
            return () => {
                document.removeEventListener('keydown', listener);
            };
        }
        const listener = handlePagingKeyDown(delayF, cancelDelayF, state, setState, imageCount, state.tab, handleHistory, ignorePopstate);
        document.addEventListener('keydown', listener);
        return () => {
            document.removeEventListener('keydown', listener);
        };
    }, [state.currentImage, state.lightboxState, state.tab, state.currentFloorPlanImage]);

    // allow opening map tab from other components
    useEffect(() => {
        const messageBus = new MessageBus();
        const openMapTab = () => {
            toggleLightbox(cancelDelayF, state, setState, isMobile, 'map', 'Kart')();
        };
        messageBus.subscribe('gallery', 'openMap', openMapTab);
        return () => {
            messageBus.unsubscribe('gallery', 'openMap', openMapTab);
        };
    }, []);

    const direction = useBreakpoints({ breakpoint: 'md' }) ? 'vertical' : 'horizontal';

    const closeButton = (
        <button
            aria-label="Lukk store bilder"
            type="button"
            className="mt-8 s-bg-inverted s-text-inverted"
            onClick={toggleLightbox(cancelDelayF, state, setState, isMobile, 'images', 'Galleri', handleHistory, ignorePopstate)}>
            <IconClose16 height="24px" width="24px" />
        </button>
    );

    let content: JSX.Element;

    const toggleButton = (text: 'overview' | 'big-images', imageCount: number) => {
        const translated = text === 'big-images' ? 'Store bilder' : 'Oversikt';
        return imageCount > 1 ? (
            <button
                type="button"
                className="mt-8 s-bg-inverted s-text-inverted flex gap-10 items-center"
                onClick={toggleOverView(setState)}>
                {text === 'overview' ? <IconGrid24 /> : <IconImage24 />}
                {translated}
            </button>
        ) : (
            <span />
        );
    };

    const noImageCount = false;
    const showHud = false;

    const closeFullscreen = () => {
        if (!document.startViewTransition) {
            setFullscreen(false);
            return;
        }

        document.startViewTransition(() => {
            flushSync(() => {
                setFullscreen(false);
            });
        });
    };

    if (state.lightboxState === 'closed') {
        content = (
            <>
                <div className="relative mb-16">
                    <ActionBar
                        itemId={itemId ?? undefined}
                        title={title}
                        userId={userId}
                        onClose={toggleLightbox(cancelDelayF, state, setState, isMobile, 'images', 'Galleri')}
                        favoriteHeartButton={favoriteHeartButton}
                        className="absolute top-0 right-0 z-10 mt-12 md:mr-24 md:mt-20 gap-8 grid grid-rows-1"
                    />
                    <div
                        className={`working cursor-pointer relative -mx-16 md:mx-0 ${useBackground ? 'md:border-around-image' : ''}`}
                        style={{ maxHeight: '1000px' }}
                        data-testid="gallery-main">
                        <MainCarousel
                            showBlur={true}
                            images={images}
                            currentImage={state.currentImage}
                            nextImage={nextImage(delayF, state, setState, imageCount)}
                            previousImage={previousImage(delayF, state, setState, imageCount)}
                            smooth={state.smooth}
                            fallbackUrl={fallbackUrl}
                            programmaticRatioOverride={programmaticRatioOverride}
                            delayF={(currentImg) => delayF(currentImg, (c: number) => updateCurrentImg(setState)(c))}
                            lightboxState={state.lightboxState}
                            programmaticRatio={state.programmaticRatio}
                            useBackground={useBackground}
                            roundAllCorners={roundAllCorners}
                            noImageCount={noImageCount}
                            newLightbox={true}
                            onImageClick={clickPhoto(delayF, cancelDelayF, state, setState, imageCount, isMobile)}
                        />
                    </div>
                </div>
                {showDescription && (
                    <p className="text-caption s-text-subtle ml-4 mt-8 truncate-mobile h-[36] md:h-[18] md:truncate">
                        {images[state.currentImage]?.description}
                    </p>
                )}
            </>
        );
    } else if (state.lightboxState === 'big-image' && !isMobile) {
        content = (
            <Lightbox>
                <div className="grid grid-cols-[auto_1fr_auto] gap-16 w-full p-16 pt-8">
                    <Tabs
                        className="inverted-tabs !mb-0 !border-0"
                        onChange={(name) => {
                            setState((s) => ({ ...s, tab: name as TabName }));
                        }}>
                        <Tab
                            label="Galleri"
                            name="images"
                            isActive={state.tab === 'images'}
                            className={`${showTabs ? 'block' : 'hidden'}`}
                        />
                        {showMap && <Tab label="Kart" name="map" isActive={state.tab === 'map'} />}
                    </Tabs>
                    <ActionBar
                        itemId={itemId ?? undefined}
                        title={title}
                        userId={userId}
                        isFullScreen={true}
                        onClose={toggleLightbox(cancelDelayF, state, setState, isMobile, 'images', 'Galleri')}
                        favoriteHeartButton={favoriteHeartButton}
                        className="flex gap-8 justify-self-end ml-auto my-auto"
                    />
                </div>
                <TabPanel name="images">
                    <div className="mx-16 relative gallery-remaining">
                        {showPanel ? (
                            <PanelGroup autoSaveId="lightbox-images" direction={direction}>
                                <Panel>
                                    <ImagesOverview
                                        images={images}
                                        onClick={handlePreviewClick(delayF, setState)}
                                        currentImage={state.currentImage}
                                        hidden={state.tab !== 'images'}
                                    />
                                </Panel>
                                <PanelResizeHandle className="gallery-resize-handle flex group items-center mx-12 my-auto justify-center resize-handle">
                                    <IconChevronLeft24 />
                                    <div className="md:w-8 md:h-[42] gallery-resize-handle-drager mx-4 rounded-full z-10"></div>
                                    <IconChevronRight24 />
                                    <div className="absolute resize-handle-line"></div>
                                </PanelResizeHandle>
                                <Panel defaultSize={85} minSize={60}>
                                    <MainCarousel
                                        showBlur={true}
                                        images={images}
                                        currentImage={state.currentImage}
                                        nextImage={nextImage(delayF, state, setState, imageCount)}
                                        previousImage={previousImage(delayF, state, setState, imageCount)}
                                        smooth={state.smooth}
                                        fallbackUrl={undefined}
                                        programmaticRatioOverride={programmaticRatioOverride}
                                        delayF={(currentImg) => delayF(currentImg, (c: number) => updateCurrentImg(setState)(c))}
                                        lightboxState={state.lightboxState}
                                        programmaticRatio={state.programmaticRatio}
                                        useBackground={useBackground}
                                        roundAllCorners={true}
                                        noImageCount={false}
                                        newLightbox={true}
                                        showHud={showHud}
                                        tab={state.tab}
                                    />
                                </Panel>
                            </PanelGroup>
                        ) : (
                            <MainCarousel
                                showBlur={true}
                                images={images}
                                currentImage={state.currentImage}
                                nextImage={nextImage(delayF, state, setState, imageCount)}
                                previousImage={previousImage(delayF, state, setState, imageCount)}
                                smooth={state.smooth}
                                fallbackUrl={undefined}
                                programmaticRatioOverride={programmaticRatioOverride}
                                delayF={(currentImg) => delayF(currentImg, (c: number) => updateCurrentImg(setState)(c))}
                                lightboxState={state.lightboxState}
                                programmaticRatio={state.programmaticRatio}
                                useBackground={useBackground}
                                roundAllCorners={true}
                                noImageCount={false}
                                newLightbox={true}
                                showHud={showHud}
                                tab={state.tab}></MainCarousel>
                        )}
                    </div>
                </TabPanel>
                <TabPanel name="map">
                    {/*<GalleryMap adId={adId} location={location} trackerVertical={trackerVertical} poiFilter={poiFilter} />*/}
                </TabPanel>
            </Lightbox>
        );
    } else if (state.lightboxState === 'big-image' && isMobile) {
        //TODO Finn bedre sted å legge til dette!
        const imageCounts = state.tab === 'images' ? `${state.currentImage + 1}/${imageCount}` : 'Bilder';
        const galleryTitle = fullscreen ? imageCounts : state.pill;

        content = (
            <Lightbox>
                <div
                    className="w-full h-full flex flex-col p-16 !s-bg-inverted gallery-transition"
                    style={{ backgroundColor: 'unset' }}
                    id="lightbox">
                    <div className="relative grid grid-cols-[auto_1fr_auto] gap-8 mb-8 items-center">
                        {fullscreen ? (
                            <Button
                                utility
                                small
                                className="!p-8 !rounded-full !max-h-[34px] button-color-dark-mode !border-0 z-20"
                                onClick={() => closeFullscreen()}
                                aria-label="Tilbake til galleri">
                                <IconChevronLeft16 className="s-icon-inverted" />
                            </Button>
                        ) : (
                            <Button
                                utility
                                small
                                aria-label="Lukk galleri"
                                className="!rounded-full inline-flex !p-8 !max-h-[34px] button-color-dark-mode !border-0 z-20"
                                onClick={toggleLightbox(
                                    cancelDelayF,
                                    state,
                                    setState,
                                    isMobile,
                                    'images',
                                    'Galleri',
                                    handleHistory,
                                    ignorePopstate,
                                )}>
                                <IconClose16 className="inline s-icon-inverted" />
                            </Button>
                        )}
                        <div className="absolute left-0 right-0 flex justify-center items-center pointer-events-none">
                            <span className="s-text-inverted text-m mb-0 font-bold">{galleryTitle}</span>
                        </div>

                        {/* Empty div for middle grid column */}
                        <div></div>

                        <div className="flex gap-8 my-auto">
                            <ActionBar
                                userId={userId}
                                itemId={itemId ?? undefined}
                                isMobile={isMobile}
                                title={title}
                                className="flex gap-8 my-auto"
                                isFullScreen={true}
                                favoriteHeartButton={favoriteHeartButton}
                                onClose={toggleLightbox(cancelDelayF, state, setState, isMobile, 'images', 'Galleri')}
                            />
                        </div>
                    </div>
                    {!fullscreen && (
                        <Tabs
                            className={`inverted-tabs-mobile !border-0 !text-caption overflow-x-auto !mb-16 whitespace-nowrap hide-scroll !-mx-16 pl-16 ${showTabs ? '!min-h-[42]' : ''}`}
                            onChange={(name) => {
                                setState((s) => ({ ...s, tab: name as TabName, pill: tabToTitle(name as TabName) }));
                            }}>
                            <Tab
                                label="Galleri"
                                name="images"
                                isActive={state.tab === 'images'}
                                className={`${showTabs ? 'block' : 'hidden'}`}
                            />
                            {showMap && <Tab label="Kart" name="map" isActive={state.tab === 'map'} />}
                            <div className="w-1 h-16 shrink-0" />
                        </Tabs>
                    )}
                    <TabPanel name="images">
                        <ImagesOverview
                            images={images}
                            onClick={handlePreviewClickMobile(delayF, setState, setFullscreen)}
                            currentImage={state.currentImage}
                            isMobile={true}
                            hidden={fullscreen}
                            showTabs={showTabs}
                        />
                        <MobileFullscreenCarousel
                            images={images}
                            currentImage={state.currentImage}
                            smooth={state.smooth}
                            fallbackUrl={undefined}
                            programmaticRatioOverride={programmaticRatioOverride}
                            delayF={(currentImg) => delayF(currentImg, (c: number) => updateCurrentImg(setState)(c))}
                            programmaticRatio={state.programmaticRatio}
                            tab={state.tab}
                            fullscreen={fullscreen}
                        />
                    </TabPanel>
                    <TabPanel name="map">
                        {/*<GalleryMap*/}
                        {/*    adId={adId}*/}
                        {/*    location={location}*/}
                        {/*    trackerVertical={trackerVertical}*/}
                        {/*    poiFilter={poiFilter}*/}
                        {/*    isMobile={true}*/}
                        {/*/>*/}
                    </TabPanel>
                </div>
                <DarkDynamicIsland />
            </Lightbox>
        );
    } else {
        content = (
            <Lightbox>
                <div className="w-full h-full" style={{ backgroundColor: 'unset' }}>
                    <div className="flex justify-between">
                        {toggleButton('big-images', imageCount)}
                        {closeButton}
                    </div>
                    <ImagesOverview
                        images={images}
                        onClick={handlePreviewClickMobile(delayF, setState, setFullscreen)}
                        currentImage={state.currentImage}
                        isMobile={isMobile}
                        hidden={fullscreen}
                        showTabs={showTabs}
                    />
                </div>
            </Lightbox>
        );
    }

    return <>{content}</>;
};

const tabToTitle = (tab: TabName) => {
    switch (tab) {
        case 'images':
            return 'Galleri';
        case 'floorplan':
            return 'Plantegning';
        case 'map':
            return 'Kart';
    }
};

const updateCurrentImg = (setState: SetState) => (currentImage: number) => {
    updateSearchParams(currentImage);
    setState((s) => ({ ...s, currentImage }));
};

const updateCurrentFloorplanImg = (setState: SetState) => (currentFloorPlanImage: number) => {
    setState((s) => ({ ...s, currentFloorPlanImage: currentFloorPlanImage }));
};

const setImageRow = (state: State, setState: SetState, imageCount: number, plusOrMinus: number) => {
    const container = document.getElementById('images-overview-grid');
    if (container) {
        const colSize = getComputedStyle(container).getPropertyValue('grid-template-columns').split(' ').length;
        const wantedImage = state.currentImage + colSize * plusOrMinus;
        let calculatedImage: number;
        if (wantedImage >= imageCount) {
            // Puts in same position on first row
            calculatedImage = wantedImage % colSize;
        } else if (wantedImage < 0) {
            const rowSize = getComputedStyle(container).getPropertyValue('grid-template-rows').split(' ').length;
            const endPos = (rowSize - 1) * colSize + state.currentImage;
            if (endPos >= imageCount) {
                calculatedImage = endPos - colSize;
            } else {
                calculatedImage = endPos;
            }
        } else {
            calculatedImage = wantedImage;
        }
        setState((s) => ({ ...s, currentImage: calculatedImage }));
    }
};

const handlePagingKeyDownFloor =
    (
        delayF: DelayFn,
        cancelDelayF: () => void,
        state: State,
        setState: SetState,
        imageCount = 0,
        handleHistory?: () => void,
        ignorePopstate?: MutableRefObject<boolean>,
    ) =>
    (event: KeyboardEvent) => {
        if (event.code === 'ArrowRight' && !event.altKey && !event.metaKey && !event.ctrlKey) {
            const tabElement = document.querySelector('div[role="tablist"]');
            const isTabInFocus = tabElement ? tabElement.contains(document.activeElement) || tabElement === document.activeElement : false;
            if (!isTabInFocus) {
                nextFloorPlanImage(delayF, state, setState, imageCount)();
            }
        }
        if (event.code === 'ArrowLeft' && !event.altKey && !event.metaKey && !event.ctrlKey) {
            const tabElement = document.querySelector('div[role="tablist"]');
            const isTabInFocus = tabElement ? tabElement.contains(document.activeElement) || tabElement === document.activeElement : false;
            if (!isTabInFocus) {
                previousFloorPlanImage(delayF, state, setState, imageCount)();
            }
        }
        if (event.code === 'ArrowUp' && !event.altKey && !event.metaKey && !event.ctrlKey) {
            event.preventDefault();
            previousFloorPlanImage(delayF, state, setState, imageCount)();
        }
        if (event.code === 'ArrowDown' && !event.altKey && !event.metaKey && !event.ctrlKey) {
            event.preventDefault();
            nextFloorPlanImage(delayF, state, setState, imageCount)();
        }

        if (event.code === 'Escape' && state.lightboxState !== 'closed') {
            toggleLightbox(cancelDelayF, state, setState, false, 'images', 'Galleri', handleHistory, ignorePopstate)();
        }
    };

const handlePagingKeyDown =
    (
        delayF: DelayFn,
        cancelDelayF: () => void,
        state: State,
        setState: SetState,
        imageCount: number,
        tab: TabName,
        handleHistory?: () => void,
        ignorePopstate?: MutableRefObject<boolean>,
    ) =>
    (event: KeyboardEvent) => {
        if (event.code === 'ArrowRight' && tab === 'images' && !event.altKey && !event.metaKey && !event.ctrlKey) {
            const tabElement = document.querySelector('div[role="tablist"]');
            const isTabInFocus = tabElement ? tabElement.contains(document.activeElement) || tabElement === document.activeElement : false;
            if (!isTabInFocus) {
                nextImage(delayF, state, setState, imageCount)();
            }
        }
        if (event.code === 'ArrowLeft' && tab === 'images' && !event.altKey && !event.metaKey && !event.ctrlKey) {
            const tabElement = document.querySelector('div[role="tablist"]');
            const isTabInFocus = tabElement ? tabElement.contains(document.activeElement) || tabElement === document.activeElement : false;
            if (!isTabInFocus) {
                previousImage(delayF, state, setState, imageCount)();
            }
        }

        if (state.lightboxState === 'overview') {
            if (event.code === 'ArrowDown') {
                setImageRow(state, setState, imageCount, 1);
            } else if (event.code === 'ArrowUp') {
                setImageRow(state, setState, imageCount, -1);
            }
        } else {
            if (
                tab === 'images' &&
                event.code === 'ArrowUp' &&
                !event.altKey &&
                !event.metaKey &&
                !event.ctrlKey &&
                state.lightboxState === 'big-image'
            ) {
                event.preventDefault();
                previousImage(delayF, state, setState, imageCount)();
            }
            if (
                tab === 'images' &&
                event.code === 'ArrowDown' &&
                !event.altKey &&
                !event.metaKey &&
                !event.ctrlKey &&
                state.lightboxState === 'big-image'
            ) {
                event.preventDefault();
                nextImage(delayF, state, setState, imageCount)();
            }
        }

        if (event.code === 'Escape' && state.lightboxState !== 'closed') {
            toggleLightbox(cancelDelayF, state, setState, false, 'images', 'Galleri', handleHistory, ignorePopstate)();
        }
        if (event.code === 'Space' && state.lightboxState !== 'closed' && imageCount > 1 && tab === 'images') {
            // prevents scrolling
            event.preventDefault();
            toggleOverView(setState)();
        }
        if (event.code === 'Enter' && state.lightboxState === 'overview') {
            // prevents scrolling
            event.preventDefault();
            toggleOverView(setState)();
        }
    };

const [debouncedReplaceHistory] = debounce((currentImage: number) => {
    try {
        const searchParams = new URLSearchParams(window.location.search);
        if (currentImage === 0) {
            searchParams.delete('ci');
        } else {
            searchParams.set('ci', (currentImage + 1).toString());
        }
        window.history.replaceState({}, '', `${window.location.pathname}?${searchParams.toString()}`);
    } catch (e) {
        console.error('Error during replace history', e);
    }
}, 500);

const updateSearchParams = (currentImage: number) => {
    debouncedReplaceHistory(currentImage);
};

export const nextImage = (delayF: DelayFn, state: State, setState: SetState, imageCount: number) => (e?: React.UIEvent | undefined) => {
    preventDefaults(e);
    const nextImg = mod(state.currentImage + 1, imageCount);
    delayF(nextImg, (i: number) => updateCurrentImg(setState)(i));
};

export const previousImage =
    (delayF: DelayFn, state: State, setState: SetState, imageCount: number) =>
    (e: UIEvent | undefined = undefined) => {
        preventDefaults(e);
        const prevImag = mod(state.currentImage - 1, imageCount);
        delayF(prevImag, (i: number) => updateCurrentImg(setState)(i));
    };

export const nextFloorPlanImage =
    (delayF: DelayFn, state: State, setState: SetState, imageCount: number) => (e?: React.UIEvent | undefined) => {
        preventDefaults(e);
        const nextImg = mod(state.currentFloorPlanImage + 1, imageCount);
        delayF(nextImg, (i: number) => updateCurrentFloorplanImg(setState)(i));
    };

export const previousFloorPlanImage =
    (delayF: DelayFn, state: State, setState: SetState, imageCount: number) =>
    (e: UIEvent | undefined = undefined) => {
        preventDefaults(e);
        const prevImag = mod(state.currentFloorPlanImage - 1, imageCount);
        delayF(prevImag, (i: number) => updateCurrentFloorplanImg(setState)(i));
    };

const toggleLightbox =
    (
        cancelDelayF: () => void,
        state: State,
        setState: SetState,
        isMobile = false,
        tab: TabName = 'images',
        pill: PillName = 'Galleri',
        handleHistory?: () => void,
        ignorePopstate?: MutableRefObject<boolean>,
        popstate?: boolean,
    ) =>
    (e: UIEvent | undefined = undefined) => {
        preventDefaults(e);
        try {
            cancelDelayF();
        } catch (e) {
            console.error('ToggleLightbox cancelDelayF error', e);
        }

        if (ignorePopstate) ignorePopstate.current = true;

        try {
            if (state.lightboxState === 'closed') {
                window.history.pushState({}, '');
            } else {
                if (!popstate) {
                    handleHistory?.();
                }
            }
        } catch (e) {
            console.error('ToggleLightbox handleHistory error', e);
        }

        try {
            setTimeout(() => {
                if (ignorePopstate) ignorePopstate.current = false;
            }, 500);
        } catch (e) {
            console.error('ToggleLightbox setTimeout ignorePopstate error', e);
        }

        try {
            if (!document.startViewTransition || (popstate && isMobile)) {
                setState((s) => ({
                    ...s,
                    lightboxState: s.lightboxState === 'closed' ? 'big-image' : 'closed',
                    tab,
                    pill,
                }));
            } else {
                if (isMobile) {
                    document.getElementById('lightbox')?.classList.add('gallery-transition');
                }
                document.startViewTransition(() => {
                    flushSync(() => {
                        setState((s) => ({
                            ...s,
                            lightboxState: s.lightboxState === 'closed' ? 'big-image' : 'closed',
                            tab,
                            pill,
                        }));
                    });
                });
            }
        } catch (error) {
            console.error('Error during view transition, falling back to simple toggle:', error);
            // Fallback: directly toggle the modal state.
            setState((s) => ({
                ...s,
                lightboxState: s.lightboxState === 'closed' ? 'big-image' : 'closed',
                tab,
                pill,
            }));
        }
    };

const clickPhoto =
    (delayF: DelayFn, cancelDelayF: () => void, state: State, setState: SetState, imageCount: number, isMobile = false) =>
    () => {
        if (state.lightboxState !== 'closed') {
            nextImage(delayF, state, setState, imageCount)();
        } else {
            toggleLightbox(cancelDelayF, state, setState, isMobile)();
        }
    };

const toggleOverView =
    (setState: SetState) =>
    (e: UIEvent | undefined = undefined) => {
        preventDefaults(e);
        setState((s) => ({
            ...s,
            lightboxState: s.lightboxState === 'overview' ? 'big-image' : 'overview',
        }));
    };

const handlePreviewClick = (_delayF: DelayFn, setState: SetState) => (e: UIEvent, page: number) => {
    preventDefaults(e);
    setState((s) => ({
        ...s,
        lightboxState: 'big-image',
        currentImage: page,
    }));
};

const handlePreviewClickMobile =
    (_delayF: DelayFn, setState: SetState, setFullscreen: (open: boolean) => void) => (e: UIEvent, page: number) => {
        preventDefaults(e);
        setState((s) => ({
            ...s,
            lightboxState: 'big-image',
            currentImage: page,
        }));
        if (!document.startViewTransition) {
            setFullscreen(true);
            return;
        }

        document.getElementById('lightbox')?.classList.remove('gallery-transition');

        document.startViewTransition(() => {
            flushSync(() => {
                setFullscreen(true);
            });
        });
    };

const setCurrentImage = (currentImage: number, apply: (page: number) => void) => {
    if (apply) {
        apply(currentImage);
    } else {
        console.error(`setCurrentImage is not defined ${currentImage}`);
    }
};

const [throttleCurrent, cancelThrottle] = throttle(setCurrentImage, 200, true) as [DelayFn, () => void];
const [debounceCurrent, cancelDebounce] = debounce(setCurrentImage, 50) as [DelayFn, () => void];

// TODO: Use it
export const getCurrentImageFromSearchParams = (searchParams: URLSearchParams) => {
    try {
        return Number.parseInt(searchParams.get('ci') || '1') - 1;
    } catch (_e) {
        return 0;
    }
};

export const preventDefaults = (e: UIEvent | undefined) => {
    e?.stopPropagation();
    e?.preventDefault();
};

type ImageGalleryProps = {
    itemId?: number;
    userId?: number;
    images: AdImage[];
    smoothOverride?: SmoothType;
    programmaticRatioOverride?: boolean;
    initialImage?: number;
    fallbackUrl?: string;
    onChange?: (state: LightboxState) => void;
    useBackground?: boolean;
    roundAllCorners: boolean;
    favoriteHeartButton?: JSX.Element;
    title?: string;
    isMobile?: boolean;
    poiFilter?: boolean;
    showSidebarForMap?: boolean;
    interactiveFloorplanFeedback?: boolean;
    showDescription?: boolean;
};

type TabName = 'images' | 'floorplan' | 'map';
export type PillName = 'Galleri' | 'Plantegning' | 'Kart';

export type SetState = React.Dispatch<React.SetStateAction<State>>;

export type State = {
    lightboxState: LightboxState;
    currentImage: number;
    currentFloorPlanImage: number;
    smooth: SmoothType;
    programmaticRatio: boolean;
    tab: TabName;
    pill: PillName;
};

export type DelayFn = (currentImage: number, apply: (i: number) => void) => void;
